import {
  useDeferredValue,
  useEffect,
  useState,
  type FC,
  type PropsWithChildren
} from 'react';
import { ReturnsFlowStateModel } from '@/services/models/Returns';
import { useLocale } from '@/react/hooks/useLocale';
import { FLOW_STATE_SESSION_KEY } from '@/services/isomorphic/ReturnsLabelService/ReturnsLabelService';
import { ReturnsFlowContext } from './ReturnsFlowContext';

/**
 * The provider of the {@link ReturnsFlowStateModel} to its children
 * which will initially load the data from the session storage (if available).
 */
export const ReturnsFlowProvider: FC<PropsWithChildren> = ({ children }) => {
  const locale = useLocale();
  const [returnsFlow, setReturnsFlow] = useState<ReturnsFlowStateModel | null>(
    // Initially `null` to avoid hydration mismatches.
    null
  );

  useEffect(
    function syncFlowFromSessionStorage() {
      /** Refresh the returns flow state from session storage. */
      function refreshReturnsFlow(): void {
        /* eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect --
          This state is client-side only, so it is safe to initialize after mount. In the future,
          we should consider a Suspense-integrated approach to initialize the UI sooner. */
        setReturnsFlow(ReturnsFlowStateModel.fromSessionStorage(locale));
      }

      refreshReturnsFlow();

      /**
       * Handle storage events to refresh the returns flow state.
       * @param event - The storage event.
       */
      function refreshReturnsFlowOnSessionStorageChanges(
        event: StorageEvent
      ): void {
        if (
          event.key === FLOW_STATE_SESSION_KEY &&
          event.storageArea === window.sessionStorage
        ) {
          refreshReturnsFlow();
        }
      }

      window.addEventListener(
        'storage',
        refreshReturnsFlowOnSessionStorageChanges
      );
      return () => {
        window.removeEventListener(
          'storage',
          refreshReturnsFlowOnSessionStorageChanges
        );
      };
    },
    [locale]
  );

  // Prevent hydration updates from triggering suspense fallbacks on dehydrated descendants.
  const deferredContextValue = useDeferredValue(returnsFlow);

  return (
    <ReturnsFlowContext value={deferredContextValue}>
      {children}
    </ReturnsFlowContext>
  );
};
